import { getUserPermissionList } from '@/repositories/userContextRepository'
import { defineStore } from 'pinia'
import { USER_ROLES } from '@/const/auth'
import { deriveDeliveryAudience } from '@/services/audience/deliveryAudience'

/**
 * User Context Store
 * Handles state management for user context, roles, and permissions
 */
export const useUserContextStore = defineStore('userContext', {
  state: () => ({
    /** User permissions object */
    permissions: null,
    /** Loading states for different operations */
    loadingStates: {
      updatingContext: false,
      updatingPermissions: false,
    },
  }),

  getters: {
    isPermissionLoaded: state => {
      return state.permissions != null
    },

    /**
     * Check if user has app permission (RUBIX app with roles)
     * @returns {boolean} True if user has RUBIX app permission
     */
    canAccessApp: state => {
      if (!state.permissions?.apps) return false

      return (
        state.permissions.apps.find(
          p => p.app && p.app.toUpperCase() === 'RUBIX',
        ) !== undefined
      )
    },

    /**
     * Check if any operation is currently loading
     * @returns {boolean} True if any operation is in loading state
     */
    isLoading: state =>
      Object.values(state.loadingStates).some(loading => loading),

    /**
     * Get all user roles from RUBIX app permissions
     * @returns {Array} Array of user role strings
     */
    roles: state => {
      if (!state.permissions?.apps) return []

      const roles = []
      state.permissions.apps.forEach(app => {
        if (app.app === 'RUBIX') {
          ;(app.roles || []).forEach(r => roles.push(r.role.toUpperCase()))
        }
      })

      return roles
    },

    /**
     * Check if user has Beta Tester role
     * @returns {boolean} True if user is a beta tester
     */
    isBetaTester() {
      return this.roles.some(r => r === USER_ROLES.BETA_TESTER)
    },

    /**
     * Check if user has REACH role
     * @returns {boolean} True if user is a REACH user
     */
    isReachUser() {
      return this.roles.some(r => r === USER_ROLES.REACH)
    },

    /**
     * Which Delivery Report a person should see.
     *
     * Derived from the roles the permissions API returned, per ADR-003. Returns
     * a token rather than a boolean, and never undefined, so the view always
     * has something defined to render.
     *
     * @returns {string} A DELIVERY_AUDIENCE token
     */
    deliveryAudience() {
      return deriveDeliveryAudience(this.roles)
    },
  },

  actions: {
    async loadPermissions() {
      try {
        const response = await getUserPermissionList()
        this.permissions = response
      } catch (error) {
        console.error('Error fetching permissions catched in store:', error)
        throw error
      }
    },

    /**
     * Check if user has any of the specified feature roles
     * @param {Array} rolesToCheck - Array of role strings to check
     * @returns {boolean} True if user has any of the specified roles
     */
    hasFeatureRoles(rolesToCheck) {
      return this.roles.some(r => rolesToCheck.includes(r))
    },

    /**
     * Reset the store to its initial state
     */
    reset() {
      this.$reset()
    },
  },
})
